import { useState, useEffect } from 'react';
import { Button, message, Upload, Modal, Cascader, Input, Select, DatePicker, Radio } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import type { UploadChangeParam } from 'antd/es/upload';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { Province } from '@/interface/area';
import { CompanyStatus } from '@/interface/company';
import { getUploadConfig } from '@/https/upload';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import Https from '@/https';
import './Company.css';

export default function CompanyCreate() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { RangePicker } = DatePicker;
  const { TextArea } = Input;
  const [messageApi, messageContextHolder] = message.useMessage();
  const UploadConfig = getUploadConfig();

  const back = () => {
    navigate(-1);
    dispatch({
      type: 'POP_MENU_KEYPATH',
      payload: { keyPath: 1, selectedKeys: 1 }
    })
  }

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const handleCancel = () => setPreviewOpen(false);

  const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      messageApi.error('只能上传JPG/PNG文件!');
    }
    const isLt20M = file.size / 1024 / 1024 < 20;
    if (!isLt20M) {
      messageApi.error('图片大小不能超过20MB!');
    }
    return isJpgOrPng && isLt20M;
  };

  const [loadingBusinessLicense, setLoadingBusinessLicense] = useState(false);
  const [businessLicense, setBusinessLicense] = useState<string>();
  const handleBusinessLicenseChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      setLoadingBusinessLicense(true);
      return;
    }
    if (info.file.status === 'done') {
      console.log('upload file', info);
      if(info.fileList[0].response) {
        setBusinessLicense(info.fileList[0].response[0]);
        setLoadingBusinessLicense(false);
      }
    }
  };

  const handleBusinessLicenseRemove = () => {
    setBusinessLicense('');
    setLoadingBusinessLicense(false);
  }

  const [loadingCorpIdPathFront, setLoadingCorpIdPathFront] = useState(false);
  const [corpIdPathFront, setCorpIdPathFront] = useState<string>();

  const handleCorpIdPathFrontChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      setLoadingCorpIdPathFront(true);
      return;
    }
    if (info.file.status === 'done') {
      console.log('upload file', info);
      if(info.fileList[0].response) {
        setCorpIdPathFront(info.fileList[0].response[0]);
        setLoadingCorpIdPathFront(false);
      }
    }
  };

  const handleCorpIdPathFrontRemove = () => {
    setCorpIdPathFront('');
    setLoadingCorpIdPathFront(false);
  }

  const [loadingCorpIdPathBehind, setLoadingCorpIdPathBehind] = useState(false);
  const [corpIdPathBehind, setCorpIdPathBehind] = useState<string>();

  const handleCorpIdPathBehindChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      setLoadingCorpIdPathBehind(true);
      return;
    }
    if (info.file.status === 'done') {
      console.log('upload file', info);
      if(info.fileList[0].response) {
        setCorpIdPathBehind(info.fileList[0].response[0]);
        setLoadingCorpIdPathBehind(false);
      }
    }
  };

  const handleCorpIdPathBehindRemove = () => {
    setCorpIdPathBehind('');
    setLoadingCorpIdPathBehind(false);
  }

  const [name, setName] = useState<string>();
  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const [account, setAccount] = useState<string>();
  const onAccountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccount(e.target.value);
  };

  const [contactName, setContactName] = useState<string>();
  const onContactNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContactName(e.target.value);
  };

  const [contactPhone, setContactPhone] = useState<string>();
  const onContactPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContactPhone(e.target.value);
  };

  const [createTime, setCreateTime] = useState<[start: Dayjs | null | undefined, end: Dayjs | null | undefined]>([undefined, undefined]);
  const onCreateTimeChange = (value: any) => {
    if(value?.length === 2) {
      setCreateTime([dayjs(value[0]), dayjs(value[1])]);
    } else {
      setCreateTime([undefined, undefined]);
    }
  }

  const [provinceCode, setProvinceCode] = useState('');
  const [cityCode, setCityCode] = useState('');
  const [regionCode, setRegionCode] = useState('');
  const [provinceAndCityCode, setProvinceAndCityCode] = useState<string[]>([]);
  const [provinceCityCodeList, setProvinceCityCodeList] = useState<Province[]>([]);
  const getProviceCityCodeList = () => {
    fetch('/pca-code.json').then(res => res.json()).then(data => {
      setProvinceCityCodeList(data);
    })
  }
  const provinceCityCodeChange = (value: string[]) => {
    console.log(value);
    if(value && value.length === 3) {
      setProvinceAndCityCode(value);
      setProvinceCode(value[0]);
      setCityCode(value[1]);
      setRegionCode(value[2]);
    } else {
      setProvinceAndCityCode([]);
      setProvinceCode('');
      setCityCode('');
      setRegionCode('');
    }
  }

  const [address, setAddress] = useState<string>();
  const onAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);
  };

  const [bankName, setBankName] = useState<string>();
  const onBankNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBankName(e.target.value);
  };

  const [accountName, setAccountName] = useState<string>();
  const onAccountNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountName(e.target.value);
  };

  const [accountNo, setAccountNo] = useState<string>();
  const onAccountNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountNo(e.target.value);
  };

  const [creditCode, setCreditCode] = useState<string>();
  const onCreditCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreditCode(e.target.value);
  };

  const [corpName, setCorpName] = useState<string>();
  const onCorpNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCorpName(e.target.value);
  };

  const [corpIdCode, setCorpIdCode] = useState<string>();
  const onCorpIdCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCorpIdCode(e.target.value);
  };

  const [corpPhone, setCorpPhone] = useState<string>();
  const onCorpPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCorpPhone(e.target.value);
  };

  const [isActive, setIsActive] = useState(1);
  const [status, setStatus] = useState<string|undefined>(undefined);
  const [companyStatusList, setCompanyStatusList] = useState<CompanyStatus[]>([]);
  const companyStatusChange = (value: string) => {
    setStatus(value);
  }

  const getCompanyStatusList = () => {
    Https.post({
      url: 'Api/sysmanagement/V1/dictionaryDtl/list',
      data: {
        dataDictionaryCode: 10009
      }
    }).then(res => {
      if(res.state === 1) {
        setCompanyStatusList(res.data.dictionaryDtlList.map((item: CompanyStatus) => {
          return {
            label: item.name,
            value: item.id
          }
        }));
      }
    })
  }

  const [remark, setRemark] = useState<string>();

  const handleSubmit = () => {
    if(!name) {
      messageApi.warning('请输入公司名称');
      return;
    }
    if(!account) {
      messageApi.warning('请输入账号');
      return;
    }
    if(!contactName) {
      messageApi.warning('请输入联系人姓名');
      return;
    }
    if(!contactPhone) {
      messageApi.warning('请输入联系人电话');
      return;
    }
    if(provinceCode === '' || cityCode === '' || regionCode === '') {
      messageApi.warning('请选择区域');
      return;
    }
    if(!status) {
      messageApi.warning('请选择公司状态');
      return;
    }
    Https.post({
      url: 'Api/Tenant/V1/tenant/AddTenant',
      data: {
        businessLicense,
        corpIdPathFront,
        corpIdPathBehind,
        name,
        account,
        contactName,
        contactPhone,
        provinceCode,
        cityCode,
        regionCode,
        address,
        bankName,
        accountName,
        accountNo,
        creditCode,
        corpName,
        corpIdCode,
        corpPhone,
        isActive,
        status,
        remark,
        beginTime: dayjs(createTime[0]).format('YYYY-MM-DD HH:mm:ss'),
        endTime: dayjs(createTime[1]).format('YYYY-MM-DD HH:mm:ss'),
        customType: 0,
      }
    }).then(res => {
      if(res.state === 1) {
        messageApi.success('添加成功');
        setTimeout(() => {
          back();
        }, 1000);
      } else {
        messageApi.error(res.data.errorMessage || res.errorMessage);
      }
    })
  }

  useEffect(() => {
    getCompanyStatusList();
    getProviceCityCodeList();
  }, [])

  return (
    <section className="Company-Create">
      <Button onClick={back} size="small" className="back-btn">返回</Button>
      <section className="create-box">
        <section className="type-imgs">
          <section className="type-img">
            <header className="info-label">营业执照</header>
            <Upload
              name="file"
              listType="picture-card"
              maxCount={1}
              className="avatar-uploader"
              showUploadList={true}
              action={UploadConfig.action}
              headers={UploadConfig.headers}
              beforeUpload={beforeUpload}
              onChange={handleBusinessLicenseChange}
              onRemove={handleBusinessLicenseRemove}
              onPreview={handlePreview}>
              { businessLicense ? null :
                <section>
                  {loadingBusinessLicense ? <LoadingOutlined /> : <PlusOutlined />}
                  <section style={{ marginTop: 8 }}>上传图片</section>
                </section>
              }
            </Upload>
          </section>
          <section className="type-img">
            <header className="info-label">身份证正面</header>
            <Upload
              name="file"
              listType="picture-card"
              maxCount={1}
              className="avatar-uploader"
              showUploadList={true}
              action={UploadConfig.action}
              headers={UploadConfig.headers}
              beforeUpload={beforeUpload}
              onChange={handleCorpIdPathFrontChange}
              onRemove={handleCorpIdPathFrontRemove}
              onPreview={handlePreview}>
              { corpIdPathFront ? null :
                <section>
                  {loadingCorpIdPathFront ? <LoadingOutlined /> : <PlusOutlined />}
                  <section style={{ marginTop: 8 }}>上传图片</section>
                </section>
              }
            </Upload>
          </section>
          <section className="type-img">
            <header className="info-label">身份证背面</header>
            <Upload
              name="file"
              listType="picture-card"
              maxCount={1}
              className="avatar-uploader"
              showUploadList={true}
              action={UploadConfig.action}
              headers={UploadConfig.headers}
              beforeUpload={beforeUpload}
              onChange={handleCorpIdPathBehindChange}
              onRemove={handleCorpIdPathBehindRemove}
              onPreview={handlePreview}>
              { corpIdPathBehind ? null :
                <section>
                  {loadingCorpIdPathBehind ? <LoadingOutlined /> : <PlusOutlined />}
                  <section style={{ marginTop: 8 }}>上传图片</section>
                </section>
              }
            </Upload>
          </section>
        </section>
        <section className="company-info">
          <section className="company-info-item">
            <span className="label required">公司名称</span>
            <Input placeholder="请输入公司名称" style={{width: 200}} value={name} allowClear onChange={onNameChange} />
          </section>
          <section className="company-info-item">
            <span className="label required">管理账号</span>
            <Input placeholder="请输入管理员账号" style={{width: 200}} value={account} allowClear onChange={onAccountChange} />
          </section>
          <section className="company-info-item">
            <span className="label">默认密码</span>
            <Input placeholder="请输入默认密码" disabled style={{width: 200}} value="heishi!Aa"/>
          </section>
          <section className="company-info-item">
            <span className="label required">联系人</span>
            <Input placeholder="请输入联系人" style={{width: 200}} value={contactName} allowClear onChange={onContactNameChange} />
          </section>
          <section className="company-info-item">
            <span className="label required">联系电话</span>
            <Input placeholder="请输入联系电话" style={{width: 200}} value={contactPhone} allowClear onChange={onContactPhoneChange} />
          </section>
          <section className="company-info-item">
            <span className="label">服务时间</span>
            <RangePicker
              allowClear
              value={createTime}
              format="YYYY-MM-DD HH:mm:ss"
              showTime={{
                defaultOpenValue:[dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')]
              }}
              onChange={onCreateTimeChange}
            />
          </section>
          <section className="company-info-item">
            <span className="label required">区域</span>
            <Cascader
              fieldNames={{ label: 'name', value: 'code' }}
              value={provinceAndCityCode}
              options={provinceCityCodeList}
              onChange={provinceCityCodeChange}
              style={{ width: 200 }}
              placeholder="请选择所属省市"
            />
          </section>
          <section className="company-info-item">
            <span className="label">详细地址</span>
            <Input placeholder="请输入详细地址" style={{width: 200}} value={address} allowClear onChange={onAddressChange} />
          </section>
          <section className="company-info-item">
            <span className="label">开户银行</span>
            <Input placeholder="请输入开户银行" style={{width: 200}} value={bankName} allowClear onChange={onBankNameChange} />
          </section>
          <section className="company-info-item">
            <span className="label">开户名称</span>
            <Input placeholder="请输入开户名称" style={{width: 200}} value={accountName} allowClear onChange={onAccountNameChange} />
          </section>
          <section className="company-info-item">
            <span className="label">收款账号</span>
            <Input placeholder="请输入收款账号" style={{width: 200}} value={accountNo} allowClear onChange={onAccountNoChange} />
          </section>
          <section className="company-info-item">
            <span className="label">信用代码</span>
            <Input placeholder="请输入信用代码" style={{width: 200}} value={creditCode} allowClear onChange={onCreditCodeChange} />
          </section>
          <section className="company-info-item">
            <span className="label">法人名字</span>
            <Input placeholder="请输入法人名字" style={{width: 200}} value={corpName} allowClear onChange={onCorpNameChange} />
          </section>
          <section className="company-info-item">
            <span className="label">身份证号</span>
            <Input placeholder="请输入法人身份证号" style={{width: 200}} value={corpIdCode} allowClear onChange={onCorpIdCodeChange} />
          </section>
          <section className="company-info-item">
            <span className="label">法人电话</span>
            <Input placeholder="请输入法人电话" style={{width: 200}} value={corpPhone} allowClear onChange={onCorpPhoneChange} />
          </section>
          <section className="company-info-item">
            <span className="label">是否有效</span>
            <Radio.Group
              value={isActive}
              options={[
                { value: 1, label: '是' },
                { value: 0, label: '否' },
              ]}
              onChange={(e) => setIsActive(e.target.value)}
            />
          </section>
          <section className="company-info-item">
            <span className="label required">公司状态</span>
            <Select
              allowClear
              placeholder="请选择公司状态"
              value={status}
              style={{ width: 200 }}
              options={companyStatusList}
              onChange={companyStatusChange}
            />
          </section>
        </section>
        <section>
          <span className="label">备注</span>
          <TextArea rows={3} value={remark} placeholder="请输入备注" onChange={(e) => setRemark(e.target.value)} />
        </section>
      </section>
      <section className="create-btn">
        <Button onClick={back}>取消</Button>
        <Button type="primary" onClick={handleSubmit}>确定</Button>
      </section>
      <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={handleCancel}>
        <img alt="example" style={{ width: '100%' }} src={previewImage} />
      </Modal>
      { messageContextHolder }
    </section>
  )
}